import { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal, Toast } from 'react-bootstrap';
import Select from 'react-select';
import Api from '../Auth/Api';
import { customStyles } from '../utils/selectStyles';
import { entrarNoContextoEmpresa } from '../utils/superAdminContexto';
import './CatalogoCursos.css';

const PLACEHOLDER_IMAGE = "https://media.istockphoto.com/id/1147544807/vector/thumbnail-image-vector-graphic.jpg?s=612x612&w=0&k=20&c=rnCKVbdxqkjlcs3xH87-9gocETqpspHFXu5dIGB4wuM=";

const TIPOS = [
    { value: 'online', label: 'Online', badge: 'bg-primary' },
    { value: 'misto', label: 'Misto', badge: 'bg-warning text-dark' },
    { value: 'presencial', label: 'Presencial', badge: 'bg-success' },
];

const pluralizar = (n, singular, plural) => `${n} ${n === 1 ? singular : plural}`;

function ResultadoSecao({ titulo, icone, cor, itens, comMensagem = false }) {
    if (itens.length === 0) return null;
    return (
        <div className="mb-3">
            <h6 className={`fw-semibold ${cor}`}><i className={`bi ${icone} me-2`} />{titulo} ({itens.length})</h6>
            <div className="list-group small" style={{ maxHeight: 180, overflowY: 'auto' }}>
                {itens.map((item, index) => (
                    <div key={`${item.curso_id}-${item.empresa_id}-${index}`} className="list-group-item">
                        <strong>{item.curso_nome}</strong> → {item.empresa_nome}
                        {comMensagem && item.message && <div className="text-danger">{item.message}</div>}
                    </div>
                ))}
            </div>
        </div>
    );
}

function CatalogoCursos() {
    const token = JSON.parse(localStorage.getItem("user_token"));

    const [carregando, setCarregando] = useState(true);
    const [catalogo, setCatalogo] = useState(null);
    const [topicos, setTopicos] = useState([]);
    const [cursos, setCursos] = useState([]);
    const [empresas, setEmpresas] = useState([]);

    const [busca, setBusca] = useState('');
    const [temaFiltro, setTemaFiltro] = useState('');
    const [tipoFiltro, setTipoFiltro] = useState('');

    // Ids dos cursos do catálogo; a seleção independe dos filtros
    const [selecionados, setSelecionados] = useState([]);

    const [entrandoCatalogo, setEntrandoCatalogo] = useState(false);
    const [cursoEmpresasId, setCursoEmpresasId] = useState(null);
    // Remoção pendente de confirmação: { curso_id, curso_nome, empresa_nome }
    const [remocao, setRemocao] = useState(null);
    const [removendo, setRemovendo] = useState(false);

    const [modalAdicionar, setModalAdicionar] = useState({ show: false, etapa: 'form' });
    const [empresasEscolhidas, setEmpresasEscolhidas] = useState([]);
    const [conflitos, setConflitos] = useState([]);
    const [enviando, setEnviando] = useState(false);
    const [resultado, setResultado] = useState(null);

    const [toast, setToast] = useState({});
    const [showToast, setShowToast] = useState(false);

    const mostrarToast = useCallback((header, body, type) => {
        setToast({ header, body, type });
        setShowToast(true);
    }, []);

    const carregarCatalogo = useCallback(async () => {
        try {
            const response = await Api.get('api/catalogo', {
                headers: { Authorization: `Bearer ${token}` }
            });
            const dados = response.data;
            const listaCursos = dados.cursos || [];
            setCatalogo(dados.catalogo ?? null);
            setTopicos(dados.topicos || []);
            setCursos(listaCursos);
            setEmpresas(dados.empresas || []);
            const idsAtuais = listaCursos.map(c => c.id);
            setSelecionados(prev => prev.filter(id => idsAtuais.includes(id)));
        } catch (error) {
            mostrarToast('Ops!', error?.response?.data?.message || 'Erro ao carregar o catálogo.', 'toast-error');
        } finally {
            setCarregando(false);
        }
    }, [token, mostrarToast]);

    useEffect(() => { carregarCatalogo(); }, [carregarCatalogo]);

    const cursosFiltrados = useMemo(() => {
        const termo = busca.trim().toLowerCase();
        return cursos.filter(c =>
            (!termo || (c.nome || '').toLowerCase().includes(termo)) &&
            (!temaFiltro || String(c.idTopico) === String(temaFiltro)) &&
            (!tipoFiltro || c.tipo === tipoFiltro)
        );
    }, [cursos, busca, temaFiltro, tipoFiltro]);

    const cursosSelecionados = cursos.filter(c => selecionados.includes(c.id));
    const cursoEmpresas = cursos.find(c => c.id === cursoEmpresasId) || null;
    const opcoesEmpresas = empresas.map(e => ({ value: e.id, label: e.nome }));

    const alternarSelecao = (id) => {
        setSelecionados(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const selecionarFiltrados = () => {
        setSelecionados(prev => [...new Set([...prev, ...cursosFiltrados.map(c => c.id)])]);
    };

    const limparSelecao = () => setSelecionados([]);

    const editarCatalogo = async () => {
        setEntrandoCatalogo(true);
        try {
            const response = await Api.post('api/selecionar-empresa', { empresa_id: catalogo.id }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            entrarNoContextoEmpresa(response.data, 'Catálogo', '/meus-cursos');
        } catch (error) {
            mostrarToast('Ops!', error?.response?.data?.message || 'Não foi possível abrir o catálogo para edição.', 'toast-error');
            setEntrandoCatalogo(false);
        }
    };

    const abrirAdicionar = () => {
        setEmpresasEscolhidas([]);
        setConflitos([]);
        setModalAdicionar({ show: true, etapa: 'form' });
    };

    const fecharAdicionar = () => {
        if (enviando) return;
        setModalAdicionar({ show: false, etapa: 'form' });
    };

    // Sem `existentes`, o backend responde 409 se algum curso já existir nas empresas escolhidas
    const enviarAdicao = async (existentes) => {
        const corpo = {
            cursos: selecionados,
            empresas: empresasEscolhidas.map(e => e.value),
        };
        if (existentes) corpo.existentes = existentes;

        setEnviando(true);
        try {
            const response = await Api.post('api/catalogo/adicionar', corpo, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setModalAdicionar({ show: false, etapa: 'form' });
            setResultado(response.data);
        } catch (error) {
            if (error?.response?.status === 409 && !existentes) {
                setConflitos(error.response.data?.conflitos || []);
                setModalAdicionar(prev => ({ ...prev, etapa: 'conflito' }));
            } else {
                mostrarToast('Ops!', error?.response?.data?.message || 'Erro ao adicionar os cursos às empresas.', 'toast-error');
                setModalAdicionar(prev => ({ ...prev, etapa: 'form' }));
            }
        } finally {
            setEnviando(false);
        }
    };

    const fecharResultado = () => {
        setResultado(null);
        setSelecionados([]);
        carregarCatalogo();
    };

    const fecharEmpresasCurso = () => {
        if (removendo) return;
        setCursoEmpresasId(null);
        setRemocao(null);
    };

    const executarRemocao = async () => {
        if (!remocao) return;
        setRemovendo(true);
        try {
            const response = await Api.delete(`api/catalogo/copias/${remocao.curso_id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            mostrarToast('Sucesso!', response.data.message, 'toast-success');
            setRemocao(null);
            await carregarCatalogo();
        } catch (error) {
            mostrarToast('Ops!', error?.response?.data?.message || 'Erro ao remover o curso da empresa.', 'toast-error');
        }
        setRemovendo(false);
    };

    return (
        <div className={`container-fluid ${selecionados.length > 0 ? 'catalogo-com-selecao' : ''}`}>
            <div className="d-flex justify-content-between align-items-center mb-4 gap-2 flex-wrap">
                <div>
                    <h4 className="mb-0 fw-bold"><i className="bi bi-collection me-2 text-primary" />Catálogo de cursos</h4>
                    {catalogo && <small className="text-muted">{pluralizar(cursos.length, 'curso', 'cursos')} no catálogo</small>}
                </div>
                {catalogo && (
                    <button type="button" className="btn btn-primary flex-shrink-0" onClick={editarCatalogo} disabled={entrandoCatalogo}>
                        {entrandoCatalogo
                            ? <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                            : <i className="bi bi-pencil-square me-2" />}
                        Editar catálogo
                    </button>
                )}
            </div>

            {carregando && (
                <div className="text-center text-muted py-5">
                    <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Carregando...</span></div>
                </div>
            )}

            {!carregando && !catalogo && (
                <div className="card border-0 shadow-sm">
                    <div className="card-body text-center text-muted py-5">
                        <i className="bi bi-collection fs-2 d-block mb-2" />
                        O catálogo de cursos ainda não foi criado.
                    </div>
                </div>
            )}

            {!carregando && catalogo && (
                <>
                    {/* Filtros */}
                    <div className="row g-2 mb-3">
                        <div className="col-12 col-md-5 col-xl-4">
                            <div className="input-group">
                                <span className="input-group-text bg-white">
                                    <i className="bi bi-search text-muted" />
                                </span>
                                <input
                                    className="form-control"
                                    placeholder="Buscar curso pelo nome..."
                                    value={busca}
                                    onChange={e => setBusca(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="col-12 col-md-4 col-xl-3">
                            <select className="form-select" value={temaFiltro} onChange={e => setTemaFiltro(e.target.value)}>
                                <option value="">Todos os temas</option>
                                {topicos.map(t => (
                                    <option key={t.id} value={t.id}>{t.nome}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-12 col-md-3 col-xl-2">
                            <select className="form-select" value={tipoFiltro} onChange={e => setTipoFiltro(e.target.value)}>
                                <option value="">Todos os tipos</option>
                                {TIPOS.map(t => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                        <small className="text-muted">Exibindo {cursosFiltrados.length} de {cursos.length} cursos</small>
                        <div className="d-flex flex-wrap gap-2">
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={selecionarFiltrados}
                                disabled={cursosFiltrados.length === 0}
                            >
                                Selecionar todos os filtrados
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={limparSelecao}
                                disabled={selecionados.length === 0}
                            >
                                Limpar seleção
                            </button>
                        </div>
                    </div>

                    {/* Grade de cursos */}
                    <div className="row g-3">
                        {cursosFiltrados.length === 0 && (
                            <div className="col-12">
                                <div className="text-center text-muted py-5">
                                    <i className="bi bi-search fs-2 d-block mb-2" />
                                    Nenhum curso encontrado para estes filtros.
                                </div>
                            </div>
                        )}

                        {cursosFiltrados.map(curso => {
                            const selecionado = selecionados.includes(curso.id);
                            const tipo = TIPOS.find(t => t.value === curso.tipo);
                            const qtdEmpresas = curso.empresas?.length || 0;

                            return (
                                <div key={curso.id} className="col-12 col-sm-6 col-lg-4 col-xl-3">
                                    <div
                                        className={`card h-100 border-2 catalogo-card ${selecionado ? 'border-primary catalogo-card-selecionado' : 'border-light'}`}
                                        role="checkbox"
                                        aria-checked={selecionado}
                                        tabIndex={0}
                                        onClick={() => alternarSelecao(curso.id)}
                                        onKeyDown={(e) => {
                                            if (e.target !== e.currentTarget) return;
                                            if (e.key === ' ' || e.key === 'Enter') {
                                                e.preventDefault();
                                                alternarSelecao(curso.id);
                                            }
                                        }}
                                    >
                                        <div className="position-relative">
                                            <img
                                                src={`${process.env.REACT_APP_IMG_URL}/${curso.thumbnail}`}
                                                className="card-img-top catalogo-thumb w-100"
                                                alt={curso.nome}
                                                onError={(e) => {
                                                    e.currentTarget.onerror = null;
                                                    e.currentTarget.src = PLACEHOLDER_IMAGE;
                                                }}
                                            />
                                            <div className="position-absolute top-0 end-0 m-2">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input catalogo-check pe-none"
                                                    checked={selecionado}
                                                    readOnly
                                                    tabIndex={-1}
                                                    aria-hidden="true"
                                                />
                                            </div>
                                        </div>

                                        <div className="card-body d-flex flex-column">
                                            <h6 className="fw-semibold mb-1">{curso.nome}</h6>
                                            <small className="text-muted mb-2">{curso.nomeTopico || 'Sem tema'}</small>

                                            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                                                {tipo && <span className={`badge ${tipo.badge}`}>{tipo.label}</span>}
                                                <small className="text-muted">
                                                    {pluralizar(curso.total_modulos ?? 0, 'módulo', 'módulos')} · {pluralizar(curso.total_aulas ?? 0, 'aula', 'aulas')}
                                                </small>
                                            </div>

                                            <div className="mt-auto">
                                                {qtdEmpresas > 0 ? (
                                                    <button
                                                        type="button"
                                                        className="badge rounded-pill bg-info-subtle text-info-emphasis border-0"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setCursoEmpresasId(curso.id);
                                                        }}
                                                    >
                                                        Em {pluralizar(qtdEmpresas, 'empresa', 'empresas')}
                                                    </button>
                                                ) : (
                                                    <span className="badge rounded-pill bg-light text-secondary border">Em nenhuma empresa</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}

            {/* Barra fixa da seleção */}
            {selecionados.length > 0 && (
                <div className="catalogo-barra-selecao bg-white shadow-lg border-top">
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 py-3 px-3">
                        <span className="fw-semibold">
                            <i className="bi bi-check2-square me-2 text-primary" />
                            {pluralizar(selecionados.length, 'curso selecionado', 'cursos selecionados')}
                        </span>
                        <div className="d-flex gap-2">
                            <button type="button" className="btn btn-outline-secondary" onClick={limparSelecao}>Limpar</button>
                            <button type="button" className="btn btn-primary" onClick={abrirAdicionar}>
                                <i className="bi bi-building-add me-2" />Adicionar às empresas
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal: adicionar cursos às empresas (e etapa de conflito) */}
            <Modal show={modalAdicionar.show} onHide={fecharAdicionar} backdrop="static" centered size="lg">
                <Modal.Header closeButton={!enviando}>
                    <span className="fw-semibold fs-5 text-primary">
                        {modalAdicionar.etapa === 'conflito' ? 'Cursos já existentes' : 'Adicionar às empresas'}
                    </span>
                </Modal.Header>
                <Modal.Body className="bg-gray-100 p-4">
                    {modalAdicionar.etapa === 'conflito' ? (
                        <>
                            <div className="alert alert-warning">
                                Alguns cursos já existem nas empresas escolhidas. Deseja duplicar?
                            </div>
                            <div className="list-group small" style={{ maxHeight: 280, overflowY: 'auto' }}>
                                {conflitos.map((c, index) => (
                                    <div key={`${c.curso_id}-${c.empresa_id}-${index}`} className="list-group-item">
                                        <strong>{c.curso_nome}</strong> → {c.empresa_nome}
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <label className="form-label text-primary fw-medium mb-0">Empresas de destino *</label>
                                <button
                                    type="button"
                                    className="btn btn-link btn-sm p-0"
                                    onClick={() => setEmpresasEscolhidas(opcoesEmpresas)}
                                    disabled={enviando || opcoesEmpresas.length === 0}
                                >
                                    Selecionar todas
                                </button>
                            </div>
                            <Select
                                isMulti
                                options={opcoesEmpresas}
                                value={empresasEscolhidas}
                                onChange={(valor) => setEmpresasEscolhidas(valor || [])}
                                styles={customStyles}
                                placeholder="Selecione as empresas"
                                closeMenuOnSelect={false}
                                isDisabled={enviando}
                                noOptionsMessage={() => 'Nenhuma empresa disponível'}
                            />

                            <label className="form-label text-primary fw-medium mt-3">
                                {pluralizar(cursosSelecionados.length, 'curso selecionado', 'cursos selecionados')}
                            </label>
                            <ul className="list-group small" style={{ maxHeight: 220, overflowY: 'auto' }}>
                                {cursosSelecionados.map(c => (
                                    <li key={c.id} className="list-group-item">
                                        {c.nome} <span className="text-muted">· {c.nomeTopico || 'Sem tema'}</span>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}

                    {enviando && (
                        <div className="alert alert-info d-flex align-items-center gap-2 mt-3 mb-0">
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                            <span>Copiando os cursos para as empresas. Isso pode levar alguns minutos, não feche esta janela.</span>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="justify-content-end">
                    {modalAdicionar.etapa === 'conflito' ? (
                        <div className="d-flex justify-content-end flex-wrap gap-2">
                            <button
                                type="button"
                                className="btn"
                                onClick={() => setModalAdicionar(prev => ({ ...prev, etapa: 'form' }))}
                                disabled={enviando}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="btn btn-outline-primary"
                                onClick={() => enviarAdicao('pular')}
                                disabled={enviando}
                            >
                                Pular os existentes
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => enviarAdicao('duplicar')}
                                disabled={enviando}
                            >
                                Duplicar
                            </button>
                        </div>
                    ) : (
                        <div className="d-flex justify-content-end gap-2">
                            <button type="button" className="btn" onClick={fecharAdicionar} disabled={enviando}>
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => enviarAdicao()}
                                disabled={enviando || empresasEscolhidas.length === 0}
                            >
                                Adicionar
                            </button>
                        </div>
                    )}
                </Modal.Footer>
            </Modal>

            {/* Modal: resultado da adição */}
            <Modal show={!!resultado} onHide={fecharResultado} centered scrollable>
                <Modal.Header closeButton>
                    <span className="fw-semibold fs-5 text-primary">Resultado da adição</span>
                </Modal.Header>
                <Modal.Body className="bg-gray-100 p-4">
                    {resultado && (
                        <>
                            {resultado.message && <p>{resultado.message}</p>}
                            <div className="d-flex flex-wrap gap-2 mb-3">
                                <span className="badge bg-success">{pluralizar(resultado.criados?.length || 0, 'criado', 'criados')}</span>
                                <span className="badge bg-secondary">{pluralizar(resultado.pulados?.length || 0, 'pulado', 'pulados')}</span>
                                <span className="badge bg-danger">{pluralizar(resultado.erros?.length || 0, 'erro', 'erros')}</span>
                            </div>
                            <ResultadoSecao titulo="Criados" icone="bi-check-circle" cor="text-success" itens={resultado.criados || []} />
                            <ResultadoSecao titulo="Pulados" icone="bi-skip-forward-circle" cor="text-secondary" itens={resultado.pulados || []} />
                            <ResultadoSecao titulo="Erros" icone="bi-x-circle" cor="text-danger" itens={resultado.erros || []} comMensagem />
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="justify-content-end">
                    <button type="button" className="btn btn-primary" onClick={fecharResultado}>Fechar</button>
                </Modal.Footer>
            </Modal>

            {/* Modal: empresas que têm este curso */}
            <Modal show={!!cursoEmpresas} onHide={fecharEmpresasCurso} centered>
                <Modal.Header closeButton={!removendo}>
                    <span className="fw-semibold fs-5 text-primary">Empresas com este curso</span>
                </Modal.Header>
                <Modal.Body className="bg-gray-100 p-4">
                    {cursoEmpresas && (
                        <>
                            <p className="mb-3"><strong>{cursoEmpresas.nome}</strong></p>

                            {remocao ? (
                                <div className="alert alert-danger mb-0">
                                    <p className="mb-3">
                                        {`Remover '${remocao.curso_nome}' da empresa '${remocao.empresa_nome}'? Matrículas, progresso e certificados deste curso nessa empresa serão apagados. Essa ação não pode ser desfeita.`}
                                    </p>
                                    <div className="d-flex justify-content-end gap-2">
                                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setRemocao(null)} disabled={removendo}>
                                            Cancelar
                                        </button>
                                        <button type="button" className="btn btn-sm btn-danger" onClick={executarRemocao} disabled={removendo}>
                                            {removendo
                                                ? <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />Removendo...</>
                                                : <><i className="bi bi-trash me-1" />Remover</>}
                                        </button>
                                    </div>
                                </div>
                            ) : cursoEmpresas.empresas.length === 0 ? (
                                <p className="text-muted mb-0">Este curso não está em nenhuma empresa.</p>
                            ) : (
                                <div className="list-group">
                                    {cursoEmpresas.empresas.map(copia => (
                                        <div key={copia.curso_id} className="list-group-item d-flex justify-content-between align-items-center">
                                            <span className="fw-semibold">{copia.empresa_nome}</span>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-danger"
                                                onClick={() => setRemocao({
                                                    curso_id: copia.curso_id,
                                                    curso_nome: cursoEmpresas.nome,
                                                    empresa_nome: copia.empresa_nome,
                                                })}
                                            >
                                                Remover
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </Modal.Body>
                {!remocao && (
                    <Modal.Footer className="justify-content-end">
                        <button type="button" className="btn btn-outline-primary" onClick={fecharEmpresasCurso}>Fechar</button>
                    </Modal.Footer>
                )}
            </Modal>

            <Toast
                show={showToast}
                onClose={() => setShowToast(false)}
                delay={4000}
                autohide
                className={toast.type}
                style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999 }}
            >
                <Toast.Header><strong className="me-auto">{toast.header}</strong></Toast.Header>
                <Toast.Body>{toast.body}</Toast.Body>
            </Toast>
        </div>
    );
}

export default CatalogoCursos;
